<script setup name="AdminScheduleRecipientListShow" lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';

import { FREQUENCY_TYPE_TO_SUCCESS_MESSAGE } from '~/constants';

import { Item, useBaseItems } from '~/features/useBaseItem';
import { useService } from '~/features/useService';
import { capitalize } from '~/features/useUtilities';

import Service from '~/services/Service';

import BaseItem from '~/components/BaseItem.vue';
import BasePage from '~/components/BasePage.vue';
import Card from '~/components/Card.vue';
import CustomCard from '~/components/CustomCard.vue';
import DataTable from '~/components/DataTable.vue';
import DataTableLoading from '~/components/DataTableLoading.vue';
import ServiceCard from '~/components/ServiceCard.vue';

import type { IDataTableSettings } from '~/types';
import type {
  IListDetails,
  IRecipientLisViewRow,
  ITradelaneAPI,
  RecurringType,
} from '~/types/adminRecipientList';

const props = defineProps<{
  id: string;
}>();

const listDetails = reactive<IListDetails>({
  name: '',
  frequency: '',
  tradelanes: [],
  createdAt: '',
  startRecurringDate: '',
});

const formatFrequency = (frequency?: RecurringType | string): string => {
  if (!frequency) return '';

  const mapped = FREQUENCY_TYPE_TO_SUCCESS_MESSAGE[frequency];
  return capitalize(mapped ?? frequency);
};

const normalizedTrades = (tradelanes: ITradelaneAPI[]): string => {
  if (!tradelanes || !tradelanes.length) return '';
  return tradelanes.map((tradelane) => tradelane.name).join(', ');
};

const listDetailsItems = computed(() =>
  useBaseItems(
    Item.label('List name').value(listDetails.name).fallback(),
    Item.label('Frequency')
      .value(formatFrequency(listDetails.frequency))
      .fallback(),
    Item.label('Created on').value(listDetails.createdAt).fallback(),
    Item.label('Start date').value(listDetails.startRecurringDate).fallback(),
    Item.label('Trades')
      .value(normalizedTrades(listDetails.tradelanes))
      .preventTruncate()
      .fallback(),
  ),
);

const tableSettings: IDataTableSettings<IRecipientLisViewRow> = {
  editable: false,
  pagination: true,
  columns: [
    {
      header: 'Email',
      key: 'email',
    },
    {
      header: 'Added on',
      key: 'createdAt',
    },
  ],
};

const tableRows = ref<IRecipientLisViewRow[]>([]);

const serviceState = useService(
  Service.recipientList().show(props.id),
  ({ data }: { data: IRecipientLisViewRow[] }) => {
    if (!data.length) return false;
    tableRows.value = data;
    return true;
  },
  {
    loading: 'skeleton',
    cancelOnNewRequest: true,
    paginated: true,
  },
);

onMounted(() => {
  Service.recipientList()
    .details(props.id)
    .onSuccess(({ data }) => {
      listDetails.name = data.name;
      listDetails.frequency = data.frequency;
      listDetails.tradelanes = data.tradelanes;
      listDetails.createdAt = data.createdAt;
      listDetails.startRecurringDate = data.startRecurringDate;
    })
    .execute();
});
</script>

<template>
  <BasePage
    title="Charterers"
    :back-button="{
      label: 'recipient list',
      to: { name: 'AdminScheduleRecipientList' },
    }"
  >
    <div class="col-span-full space-y-2">
      <Card title="List Details">
        <div class="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <BaseItem
            v-for="item in listDetailsItems"
            v-bind="item"
            :key="item.key"
          />
        </div>
      </Card>

      <CustomCard data-test="recipient-view-table" title="Recipient List">
        <ServiceCard
          v-bind="serviceState"
          noResultsMessage="Please amend the search criteria or filter options to see available data."
          styled
        >
          <DataTableLoading
            v-if="serviceState.stateIsLoading"
            :columns="tableSettings.columns.length"
          />

          <DataTable :settings="tableSettings" :data="tableRows" />
        </ServiceCard>
      </CustomCard>
    </div>
  </BasePage>
</template>
